import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { AgricolaProduct, PolicyDocuments, SellReportRow } from '../../../interfaces/agricola.interface';
import { AgricolaService } from '../../../services/agricola.service';
import { describeDocumentError } from '../../../utils/document-error.util';
import { AgricolaReportConfirmDialogComponent } from './agricola-report-confirm-dialog/agricola-report-confirm-dialog.component';

const PAGE_SIZE = 50;

@Component({
  selector: 'app-agricola-sell-report',
  templateUrl: './agricola-sell-report.component.html',
  styleUrls: ['./agricola-sell-report.component.css'],
  standalone: false
})
export class AgricolaSellReportComponent implements OnInit {
  readonly pageSize = PAGE_SIZE;

  fromDate = '';
  toDate = '';

  availableProducts: AgricolaProduct[] = [];
  selectedProductIds = new Set<number>();
  productsDropdownOpen = false;
  @ViewChild('productsField') productsField?: ElementRef<HTMLElement>;

  // Optional agency filter. Empty string = all agencies (no filter sent upstream).
  agencyOptions: { value: string; label: string }[] = [];
  selectedAgencyCode = '';

  rows: SellReportRow[] = [];
  total = 0;
  page = 1;

  loadingReport = false;
  exporting = false;
  errorMessage = '';
  hasSearched = false;

  // Feature 2: per-row signature/certificate menu. The companion call is lazy —
  // fired only when a row's menu opens — and cached by policy number.
  // Column is enabled: it renders Descargar firma + Ver/Descargar certificado.
  // "Ver firma" is intentionally hidden (see the template).
  readonly showRowActions = true;
  openMenuPolicy: string | null = null;
  private readonly policyDocsCache = new Map<string, PolicyDocuments>();
  private readonly policyDocsLoading = new Set<string>();

  // Own signature preview modal (CSP-safe: data: URL on an <img>, never blob:).
  signatureModalOpen = false;
  signatureModalPolicy = '';
  signatureModalUrl: SafeUrl | null = null;
  signatureModalLoading = false;
  signatureModalError = '';

  actionError = '';

  // Certificate open/download: status-aware error + per-row manual retry, mirroring
  // the sale wizard. The emitted document can be "listo a los segundos" (apiserver
  // cold start / PDF still rendering), so the seller must be able to retry the exact
  // action that failed without reopening the row menu.
  certificateError = '';
  certificateRetrying = false;
  private certificateRetry: (() => Promise<void>) | null = null;

  constructor(
    private readonly agricola: AgricolaService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    const today = this.formatDate(new Date());
    this.fromDate = today;
    this.toDate = today;

    // El reporte cubre asistencias y seguros, así que el filtro lista el catálogo
    // completo. Sin productos seleccionados el filtro va como 'todos' y el alcance
    // lo decide el reporte upstream.
    this.agricola.getProducts().subscribe({
      next: products => { this.availableProducts = products; },
      error: () => { this.availableProducts = []; }
    });

    this.agricola.getAgencyOptions().subscribe({
      next: options => { this.agencyOptions = options; },
      error: () => { this.agencyOptions = []; }
    });
  }

  get selectedProducts(): AgricolaProduct[] {
    return this.availableProducts.filter(p => this.selectedProductIds.has(p.id));
  }

  get selectedProductsLabel(): string {
    const selected = this.selectedProducts;
    if (!selected.length) {
      return 'Todos los productos';
    }
    if (selected.length === 1) {
      return selected[0].product_name;
    }
    return `${selected.length} productos seleccionados`;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get rangeEnd(): number {
    return Math.min(this.page * this.pageSize, this.total);
  }

  toggleProductsDropdown(): void {
    this.productsDropdownOpen = !this.productsDropdownOpen;
  }

  // El desplegable es propio (no un <select>), así que hay que cerrarlo a mano
  // al hacer click fuera; los clicks dentro del campo ya los maneja el toggle o
  // el checkbox de cada opción.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.productsDropdownOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (target && this.productsField?.nativeElement.contains(target)) {
      return;
    }

    this.productsDropdownOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.productsDropdownOpen = false;
  }

  toggleProduct(product: AgricolaProduct): void {
    if (this.selectedProductIds.has(product.id)) {
      this.selectedProductIds.delete(product.id);
    } else {
      this.selectedProductIds.add(product.id);
    }
  }

  isProductSelected(product: AgricolaProduct): boolean {
    return this.selectedProductIds.has(product.id);
  }

  generate(page = 1): void {
    if (!this.validateDates()) {
      return;
    }
    this.page = page;
    this.loadingReport = true;
    this.errorMessage = '';
    this.hasSearched = true;

    const filter = this.agricola.buildSellReportFilter(this.fromDate, this.toDate, this.selectedProducts, this.selectedAgencyCode);

    this.agricola.getSellReport(filter, page, this.pageSize).subscribe({
      next: ([rows, total]) => {
        this.rows = rows || [];
        this.total = total || 0;
        this.loadingReport = false;
      },
      error: () => {
        this.rows = [];
        this.total = 0;
        this.loadingReport = false;
        this.errorMessage = 'No se pudo generar el reporte. Intenta nuevamente.';
      }
    });
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }
    this.generate(page);
  }

  exportReport(): void {
    if (!this.validateDates()) {
      return;
    }
    this.exporting = true;
    this.errorMessage = '';

    const filter = this.agricola.buildSellReportFilter(this.fromDate, this.toDate, this.selectedProducts, this.selectedAgencyCode);

    this.agricola.exportSellReport(filter).subscribe({
      next: report => {
        this.exporting = false;
        this.openConfirmModal(report?.id ?? '');
      },
      error: () => {
        this.exporting = false;
        this.errorMessage = 'No se pudo solicitar la descarga. Intenta nuevamente.';
      }
    });
  }

  estadoLabel(estado: number): string {
    return estado === 1 ? 'Activa' : 'Cancelada';
  }

  // --- Feature 2: signature / certificate menu ----------------------------------

  toggleRowMenu(row: SellReportRow): void {
    const policy = row.Solicitud;
    if (this.openMenuPolicy === policy) {
      this.openMenuPolicy = null;
      return;
    }
    this.openMenuPolicy = policy;
    this.actionError = '';
    void this.ensurePolicyDocs(policy);
  }

  closeRowMenu(): void {
    this.openMenuPolicy = null;
  }

  // Lazily loads (and caches) the companion record for a policy. Never throws:
  // getPolicyDocuments already normalizes "no evidence" to has_signature=false.
  private async ensurePolicyDocs(policy: string): Promise<void> {
    if (!policy || this.policyDocsCache.has(policy) || this.policyDocsLoading.has(policy)) {
      return;
    }
    this.policyDocsLoading.add(policy);
    try {
      const docs = await this.agricola.getPolicyDocuments(policy);
      this.policyDocsCache.set(policy, docs);
    } finally {
      this.policyDocsLoading.delete(policy);
    }
  }

  isDocsLoading(row: SellReportRow): boolean {
    return this.policyDocsLoading.has(row.Solicitud);
  }

  hasSignature(row: SellReportRow): boolean {
    return !!this.policyDocsCache.get(row.Solicitud)?.has_signature;
  }

  async viewSignature(row: SellReportRow): Promise<void> {
    const docs = this.policyDocsCache.get(row.Solicitud);
    const attachmentId = docs?.signature_attachment_id;
    if (!attachmentId) {
      return;
    }
    this.openMenuPolicy = null;
    this.signatureModalOpen = true;
    this.signatureModalPolicy = row.Solicitud;
    this.signatureModalUrl = null;
    this.signatureModalError = '';
    this.signatureModalLoading = true;
    try {
      // data: URL (not blob:) — the app CSP allows img-src data: but not blob:.
      const dataUrl = await this.agricola.fetchAttachmentDataUrl(attachmentId);
      this.signatureModalUrl = this.sanitizer.bypassSecurityTrustUrl(dataUrl);
    } catch {
      this.signatureModalError = 'No se pudo cargar la firma. Intenta nuevamente.';
    } finally {
      this.signatureModalLoading = false;
    }
  }

  closeSignatureModal(): void {
    this.signatureModalOpen = false;
    this.signatureModalUrl = null;
    this.signatureModalError = '';
    this.signatureModalPolicy = '';
  }

  async downloadSignature(row: SellReportRow): Promise<void> {
    const docs = this.policyDocsCache.get(row.Solicitud);
    const attachmentId = docs?.signature_attachment_id;
    if (!attachmentId) {
      return;
    }
    this.openMenuPolicy = null;
    this.actionError = '';
    try {
      await this.agricola.downloadAttachment(attachmentId, 1);
    } catch {
      this.actionError = 'No se pudo descargar la firma. Intenta nuevamente.';
    }
  }

  // Certificate: prefer the signed PDF attachment when present, otherwise fall back
  // to the emitted policy document (document_without_signature_auth) by policy number.
  async viewCertificate(row: SellReportRow): Promise<void> {
    this.openMenuPolicy = null;
    await this.runCertificateAction(row, () => this.doViewCertificate(row));
  }

  async downloadCertificate(row: SellReportRow): Promise<void> {
    this.openMenuPolicy = null;
    await this.runCertificateAction(row, () => this.doDownloadCertificate(row));
  }

  // Retries the exact certificate action (view or download, same row) that last
  // failed. Bound to the "Reintentar" button in the error box.
  async retryCertificate(): Promise<void> {
    if (!this.certificateRetry || this.certificateRetrying) {
      return;
    }
    this.certificateRetrying = true;
    const action = this.certificateRetry;
    try {
      await action();
    } finally {
      this.certificateRetrying = false;
    }
  }

  // Runs a certificate action, clearing any prior error on success and recording a
  // status-aware message + the retry closure on failure.
  private async runCertificateAction(row: SellReportRow, action: () => Promise<void>): Promise<void> {
    this.certificateError = '';
    try {
      await action();
      this.certificateRetry = null;
    } catch (error) {
      this.certificateError = describeDocumentError(error, row.Solicitud);
      this.certificateRetry = action;
    }
  }

  private async doViewCertificate(row: SellReportRow): Promise<void> {
    const docs = this.policyDocsCache.get(row.Solicitud);
    if (docs?.signed_document_attachment_id) {
      await this.agricola.viewAttachment(docs.signed_document_attachment_id);
    } else {
      await this.agricola.downloadPolicy(row.Solicitud);
    }
  }

  private async doDownloadCertificate(row: SellReportRow): Promise<void> {
    const docs = this.policyDocsCache.get(row.Solicitud);
    if (docs?.signed_document_attachment_id) {
      await this.agricola.downloadAttachment(docs.signed_document_attachment_id, 1);
    } else {
      await this.agricola.downloadPolicyDocumentFile(row.Solicitud);
    }
  }

  goBack(): void {
    this.router.navigate(['/agricola']);
  }

  private openConfirmModal(requestId: number | string): void {
    const dialogRef = this.dialog.open(AgricolaReportConfirmDialogComponent, {
      width: '420px',
      data: { requestId }
    });

    dialogRef.afterClosed().subscribe(accepted => {
      if (accepted) {
        this.router.navigate(['/agricola/reportes/cola']);
      }
    });
  }

  private validateDates(): boolean {
    if (!this.fromDate || !this.toDate) {
      this.errorMessage = 'Selecciona las fechas desde y hasta.';
      return false;
    }
    if (this.fromDate > this.toDate) {
      this.errorMessage = 'La fecha "Desde" no puede ser mayor que la fecha "Hasta".';
      return false;
    }
    return true;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
